/**
 * useLoadingPlayback
 *
 * Pure React hook for the Loading Playback System.
 * Completely independent of the optimization engine.
 *
 * Designed for future extension:
 *   - PlaybackMode: 'loading' | 'unloading' | 'replay' | 'backward'
 *   - Multi-stop routes (multiple placement arrays)
 *   - Group-by-group or pallet-by-pallet loading
 */

import { useState, useRef, useEffect, useCallback } from 'react'

// ── Types ─────────────────────────────────────────────────────────────────────

export type PlaybackSpeed = 0.5 | 1 | 2 | 5

/**
 * PlaybackMode — union ready for future animation modes.
 * Only 'loading' is implemented in v1.
 */
export type PlaybackMode = 'loading' | 'unloading' | 'replay' | 'backward'

export interface UseLoadingPlaybackOptions {
  /** Total number of placements / animation steps */
  totalSteps: number
  /**
   * Initial step.
   * - undefined / totalSteps → all items visible (default, no regression)
   * - 0 → all items hidden (start of animation)
   */
  initialStep?: number
  /** Base interval in ms at speed 1×. Default: 600 */
  baseIntervalMs?: number
}

export interface UseLoadingPlaybackReturn {
  /** Current visible step (0 = none, totalSteps = all) */
  step: number
  totalSteps: number
  isPlaying: boolean
  speed: PlaybackSpeed
  mode: PlaybackMode
  /** True when step === totalSteps */
  isAtEnd: boolean
  /** True when step === 0 */
  isAtStart: boolean
  /** Percentage 0–100 for progress display */
  progress: number

  // Controls
  play: () => void
  pause: () => void
  /** Reset to step 0 (all items hidden) */
  reset: () => void
  nextStep: () => void
  prevStep: () => void
  /** Seek to an arbitrary step */
  seek: (n: number) => void
  setSpeed: (s: PlaybackSpeed) => void
  /** For future use: switch between loading/unloading/etc */
  setMode: (m: PlaybackMode) => void
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useLoadingPlayback({
  totalSteps,
  initialStep,
  baseIntervalMs = 600,
}: UseLoadingPlaybackOptions): UseLoadingPlaybackReturn {
  // Default: show all items (no regression vs existing viewer)
  const defaultStep = initialStep !== undefined ? initialStep : totalSteps

  const [step, setStep] = useState<number>(() => Math.min(defaultStep, totalSteps))
  const [isPlaying, setIsPlaying] = useState(false)
  const [speed, setSpeedState] = useState<PlaybackSpeed>(1)
  const [mode, setModeState] = useState<PlaybackMode>('loading')

  // Keep totalSteps in sync if it changes (e.g. run reloaded)
  // When totalSteps changes from 0 to a positive value (data loaded), reset step to totalSteps
  const totalStepsRef = useRef(totalSteps)
  useEffect(() => {
    const wasEmpty = totalStepsRef.current === 0
    totalStepsRef.current = totalSteps
    if (wasEmpty && totalSteps > 0) {
      // Data just loaded: show all items
      setStep(totalSteps)
    } else {
      setStep(prev => Math.min(prev, totalSteps))
    }
  }, [totalSteps])

  // ── Timer ──────────────────────────────────────────────────────────────────

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const clearTimer = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  const startTimer = useCallback((currentSpeed: PlaybackSpeed) => {
    clearTimer()
    const ms = Math.round(baseIntervalMs / currentSpeed)
    intervalRef.current = setInterval(() => {
      setStep(prev => {
        const next = prev + 1
        if (next >= totalStepsRef.current) {
          // Reached the end — stop playback
          clearInterval(intervalRef.current!)
          intervalRef.current = null
          setIsPlaying(false)
          return totalStepsRef.current
        }
        return next
      })
    }, ms)
  }, [baseIntervalMs, clearTimer])

  // Clean up on unmount
  useEffect(() => () => clearTimer(), [clearTimer])

  // ── Controls ───────────────────────────────────────────────────────────────

  const play = useCallback(() => {
    setStep(prev => {
      // If already at end, auto-reset to 0 then play
      const start = prev >= totalStepsRef.current ? 0 : prev
      return start
    })
    setIsPlaying(true)
    // Timer started in effect below when isPlaying flips
  }, [])

  const pause = useCallback(() => {
    setIsPlaying(false)
    clearTimer()
  }, [clearTimer])

  const reset = useCallback(() => {
    setIsPlaying(false)
    clearTimer()
    setStep(0)
  }, [clearTimer])

  const nextStep = useCallback(() => {
    pause()
    setStep(prev => Math.min(prev + 1, totalStepsRef.current))
  }, [pause])

  const prevStep = useCallback(() => {
    pause()
    setStep(prev => Math.max(prev - 1, 0))
  }, [pause])

  const seek = useCallback((n: number) => {
    pause()
    setStep(Math.max(0, Math.min(n, totalStepsRef.current)))
  }, [pause])

  const setSpeed = useCallback((s: PlaybackSpeed) => {
    setSpeedState(s)
    // Restart timer at new speed if currently playing
    if (isPlaying) {
      startTimer(s)
    }
  }, [isPlaying, startTimer])

  const setMode = useCallback((m: PlaybackMode) => {
    pause()
    setModeState(m)
  }, [pause])

  // ── Effect: start/stop timer when isPlaying changes ───────────────────────

  useEffect(() => {
    if (isPlaying) {
      startTimer(speed)
    } else {
      clearTimer()
    }
    return clearTimer
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying])

  // ── Derived ────────────────────────────────────────────────────────────────

  const isAtEnd   = step >= totalSteps
  const isAtStart = step === 0
  const progress  = totalSteps > 0 ? Math.round((step / totalSteps) * 100) : 100

  return {
    step,
    totalSteps,
    isPlaying,
    speed,
    mode,
    isAtEnd,
    isAtStart,
    progress,

    play,
    pause,
    reset,
    nextStep,
    prevStep,
    seek,
    setSpeed,
    setMode,
  }
}
